/**
 * The agent's text field — the one place a customer types.
 *
 * It is a textarea, not a single-line input, and it GROWS as the description gets
 * longer: someone explaining what they are building writes several sentences, and
 * they must be able to see all of it. Height follows the content up to a cap, after
 * which it scrolls, so the panel can never be pushed off the screen.
 *
 * Enter sends. Shift+Enter starts a new line, because this is a field people write
 * paragraphs into.
 */
import { useCallback, useLayoutEffect, useRef, useState, type FC, type FormEvent } from 'react';
import { ArrowRight } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@/app/lib/utils';

const messages = defineMessages({
  send: {
    id: 'agentInput.send',
    defaultMessage: 'Send',
    description: 'Accessible label of the button that sends what the customer typed',
  },
});

/** Grows to fit the content, then scrolls. Roughly 9 lines on the landing. */
const MAX_HEIGHT_PX = 208;

/**
 * How the submit arrow reads, and it must answer one question at a glance: is
 * there anything to send right now?
 *
 *   empty — outlined and dim. Nothing to do, and the button is genuinely disabled.
 *   typed — FILLED. The moment a non-whitespace character exists the arrow takes a
 *           soft fill and its border and glyph come up to full strength, so the way
 *           forward is unmistakable. It is a quiet fill rather than a solid white
 *           slab: it must be obvious, not the loudest thing in the panel.
 *   hover — only then does it go solid, as the ordinary press affordance.
 *
 * Extracted and exported so the two states are unit-testable: this is pure class
 * selection, with no DOM or typing needed to prove it flips.
 */
export function submitArrowClass(ready: boolean): string {
  return ready
    ? 'border-panel-ink bg-panel-ink/15 text-panel-ink hover:bg-panel-ink hover:text-panel'
    : 'border-panel-line text-panel-ink/40';
}

export const AgentInput: FC<{
  placeholder: string;
  onSubmit: (text: string) => void;
  /** `panel` is the compact variant used beside a question. */
  size?: 'landing' | 'panel';
  className?: string;
}> = ({ placeholder, onSubmit, size = 'landing', className }) => {
  const intl = useIntl();
  const [draft, setDraft] = useState('');
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);

  // Measure after every change: reset to `auto` first so the box can shrink again
  // when text is deleted, not only grow.
  const resize = useCallback(() => {
    const field = fieldRef.current;
    if (!field) {
      return;
    }
    field.style.height = 'auto';
    const next = Math.min(field.scrollHeight, MAX_HEIGHT_PX);
    field.style.height = `${next}px`;
    field.style.overflowY = field.scrollHeight > MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  useLayoutEffect(resize, [draft, resize]);

  const send = () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    setDraft('');
    onSubmit(text);
  };

  /** Whether there is genuinely something to send — drives the arrow's fill. */
  const ready = draft.trim().length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send();
  };

  return (
    <form onSubmit={submit} className={cn('flex items-end gap-2', className)}>
      <textarea
        ref={fieldRef}
        rows={1}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          'min-w-0 flex-1 resize-none rounded-sm border border-panel-line bg-transparent text-panel-ink placeholder:text-muted-foreground-subtle hover:border-panel-ink/60 focus:border-panel-ink focus:outline-none',
          size === 'landing' ? 'px-3 py-2.5 text-sm leading-relaxed' : 'px-3 py-2 text-sm leading-relaxed',
        )}
      />
      <button
        type="submit"
        aria-label={intl.formatMessage(messages.send)}
        disabled={!ready}
        className={cn(
          'flex flex-none items-center justify-center rounded-sm border transition-colors duration-150 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[3px] focus-visible:outline-panel-ink/60',
          size === 'landing' ? 'h-9 w-9' : 'h-8 w-8',
          /* Empty: outlined and subdued. The moment there is something to send the
             arrow fills in, so the way forward is obvious without shouting. */
          submitArrowClass(ready),
        )}
      >
        <ArrowRight className={size === 'landing' ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden="true" />
      </button>
    </form>
  );
};
